import { DateTime } from 'luxon';
import { DeadlineType } from '@/modules/common/enums/deadline-type.ts';
import { DocumentStatusId } from '@/modules/common/enums/document-status-id.enum.ts';
import { ParameterType } from '@/modules/common/enums/parameter-type.ts';
import type { SQLDate } from '@/modules/common/types/sql-date.type.ts';
import type { Deadline } from '@/modules/common/types/ui.ts';
import { ColorCode } from '@/modules/core/enums/color-code.enum.ts';
import type { DocumentMask } from '@/modules/document/adapters/document-mask.type.ts';
import type { MaskFormData, MaskFormValue } from '@/modules/document/document-master/types/mask.ts';
import type { Parameter } from '@/modules/document/models/parameter.ts';
import type { DocumentInternalDto } from '@/modules/document/dto/document-internal.dto.ts';
import type { DocumentEditParams } from '@/modules/document/types/document-edit.type.ts';
import type {
  DocumentEditFormState,
  DocumentLaunchMode,
} from '@/modules/document/document-edit/types/document-edit-form.type.ts';
import { createLaunchDeadline as createDefaultLaunchDeadline } from '@/modules/document/composables/use-launch-options.ts';
import { readPaddedMaskValue } from '@/modules/ui/utils/mask-caret.util.ts';
import type { RegistrationFieldsValue } from '@/modules/document/components/registration/registration-fields.type.ts';

const CONTENT_DATE_FORMAT = 'dd.MM.yyyy';
const MASKED_CONTENT_PLACEHOLDER = '-';

function fromSQL(value: SQLDate | null) {
  if (!value) {
    return null;
  }

  const date = DateTime.fromSQL(value);

  return date.isValid ? date : null;
}

function parseParameterValue(parameter: Parameter, raw: string): MaskFormValue | undefined {
  const value = raw.trim();

  if (!value) {
    return undefined;
  }

  switch (parameter.type) {
    case ParameterType.Date: {
      const date = DateTime.fromFormat(value, CONTENT_DATE_FORMAT);
      return date.isValid ? date : undefined;
    }
    case ParameterType.Integer:
    case ParameterType.Decimal: {
      const number = Number(value.replace(',', '.'));
      return Number.isFinite(number) ? number : undefined;
    }
    case ParameterType.String: {
      if (parameter.isList) {
        return { code: value, label: value };
      }

      const masked = parameter.inputMask ? readPaddedMaskValue(parameter.inputMask, raw) : null;

      return masked === null ? value : masked || undefined;
    }
    default:
      return value;
  }
}

export function parseMaskFormData(
  parameters: readonly Parameter[],
  content: string,
  mask: DocumentMask | undefined
): MaskFormData {
  const formData: MaskFormData = {};

  if (!mask) {
    return formData;
  }

  parameters.forEach((parameter, index) => {
    const position = mask.params[index];

    if (!position) {
      return;
    }

    const value = parseParameterValue(parameter, content.slice(position.start, position.end));

    if (value !== undefined) {
      formData[parameter.number] = value;
    }
  });

  return formData;
}

export function createDeadline(value: SQLDate | null): Deadline {
  const date = fromSQL(value);

  return date
    ? { type: DeadlineType.Date, days: 1, date }
    : { type: DeadlineType.None, days: 1, date: DateTime.now().plus({ days: 1 }) };
}

export function createLaunchDeadline(value: SQLDate | null): Deadline {
  const deadline = createDeadline(value);

  return deadline.type === DeadlineType.None ? { ...deadline, type: DeadlineType.Date } : deadline;
}

export function createRegistration(snapshot: DocumentInternalDto): RegistrationFieldsValue {
  return { number: snapshot.Номер ?? '', date: fromSQL(snapshot.Регистрация), auto: false };
}

export function getPendingLaunchDate(value: SQLDate | null) {
  const launch = fromSQL(value);

  return launch && launch.startOf('day') > DateTime.now().startOf('day') ? launch : null;
}

export function getLaunchMode(status: DocumentStatusId | null | undefined): DocumentLaunchMode {
  return status === DocumentStatusId.Draft || status === DocumentStatusId.Paused ? 'launch' : 'control';
}

export function createDelayedLaunch(
  snapshot: DocumentInternalDto,
  systemUserId: number
): Pick<DocumentEditFormState, 'delayedLaunch' | 'delayedDeadline' | 'stopControlBeforeLaunch'> {
  const pendingDate = getPendingLaunchDate(snapshot.Запуск);

  return {
    delayedLaunch: pendingDate !== null,
    delayedDeadline: pendingDate
      ? { type: DeadlineType.Date, days: 1, date: pendingDate }
      : createDefaultLaunchDeadline(),
    stopControlBeforeLaunch: pendingDate !== null && snapshot.CONTROLLER_ID === systemUserId,
  };
}

type LaunchPlan = {
  holdControl: boolean;
  startDate: DateTime | null;
  deactivate: boolean;
};

function planLaunch(form: DocumentEditFormState, snapshot: DocumentInternalDto, mode: DocumentLaunchMode): LaunchPlan {
  if (mode === 'launch') {
    return {
      holdControl: form.delayedLaunch && form.stopControlBeforeLaunch,
      startDate: form.delayedLaunch ? form.delayedDeadline.date : null,
      deactivate: form.delayedLaunch,
    };
  }

  return {
    holdControl: form.removedFromControl,
    startDate: form.removedFromControl ? form.launch.date : getPendingLaunchDate(snapshot.Запуск),
    deactivate: form.removedFromControl,
  };
}

export function isRemovedFromControl(snapshot: DocumentInternalDto, systemUserId: number) {
  return snapshot.CONTROLLER_ID === systemUserId && getPendingLaunchDate(snapshot.Запуск) !== null;
}

export function getLostRegistrationTime(snapshot: DocumentInternalDto, date: DateTime | null) {
  const original = fromSQL(snapshot.Регистрация);

  return original && date && original.hasSame(date, 'day') && !original.equals(original.startOf('day'))
    ? original
    : null;
}

function getEditColor({ color, colorLocked }: DocumentEditFormState) {
  if (colorLocked) {
    return color ?? ColorCode.None;
  }

  return color === ColorCode.None ? null : color;
}

export function buildEditParams(
  form: DocumentEditFormState,
  snapshot: DocumentInternalDto,
  mode: DocumentLaunchMode = 'control'
): DocumentEditParams {
  if (!form.project || !form.controller) {
    throw new Error('The project and the controller are required to edit a document.');
  }

  const hasMask = form.mask.parameters.length > 0;
  const color = getEditColor(form);
  const launch = planLaunch(form, snapshot, mode);

  return {
    documentId: snapshot.DOC_ID,
    typeId: form.type.code,
    projectId: form.project.code,
    controllerId: form.controller.code,
    content: hasMask ? snapshot.Содержание || MASKED_CONTENT_PLACEHOLDER : form.content.trim(),
    comment: form.comment.trim() || null,
    deadline: form.deadline.type === DeadlineType.None ? null : form.deadline.date,
    startDate: launch.startDate,
    registration: {
      number: form.registration.number.trim() || null,
      date: form.registration.date,
    },
    color,
    colorLocked: form.colorLocked,
    mask: hasMask ? { parameters: form.mask.parameters, formData: form.mask.formData } : null,
  };
}

export type EditSavePlan = {
  params: DocumentEditParams;
  registration: { number: string; date: DateTime } | null;
  removeFromControl: boolean;
  launchDate: DateTime | null;
  pause: boolean;
};

export function planSave(
  form: DocumentEditFormState,
  snapshot: DocumentInternalDto,
  systemUserId: number | null,
  returnControllerId: number | null,
  mode: DocumentLaunchMode = 'control'
): EditSavePlan {
  const params = buildEditParams(form, snapshot, mode);
  const launch = planLaunch(form, snapshot, mode);
  const selectedControllerId = params.controllerId;
  const returnTargetId =
    selectedControllerId === systemUserId && returnControllerId !== null ? returnControllerId : selectedControllerId;
  const staysRemoved =
    launch.holdControl &&
    snapshot.CONTROLLER_ID === systemUserId &&
    (selectedControllerId === systemUserId || returnTargetId === returnControllerId);
  const controllerId = staysRemoved ? snapshot.CONTROLLER_ID : returnTargetId;
  const { number, date } = params.registration;
  const registrationTime = form.registration.auto || !number ? null : getLostRegistrationTime(snapshot, date);

  return {
    params: { ...params, controllerId },
    registration: number && registrationTime ? { number, date: registrationTime } : null,
    removeFromControl: launch.holdControl && !staysRemoved,
    launchDate: controllerId !== snapshot.CONTROLLER_ID ? launch.startDate : null,
    pause: launch.deactivate && snapshot.Активен === 1,
  };
}
